import { Autocomplete as Muiautocomplete, ListItem, TextField } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { t } from 'i18next';
import { SyntheticEvent } from 'react';

type CustomAutocompleteProps<T> = {
  error?: string;
  disabled?: boolean;
  options: T[];
  value: T | null;
  optionLabel: (option: T) => string;
  placeholder?: string;
  onSelect: (value: T | null) => void;
  noOptionsText?: string;
  onInputChange?: (value: string) => void;
  isDebouncing?: boolean;
  inputValue: string
  maxLength?: number,
  disablePortal?: boolean
};

export const CustomAutocomplete = <T extends { name: string }, >({
  value,
  options,
  optionLabel,
  placeholder,
  onSelect,
  noOptionsText,
  onInputChange,
  disabled,
  error,
  inputValue,
  isDebouncing,
  maxLength,
  disablePortal
}: CustomAutocompleteProps<T>) => {
  const handleInputChange = onInputChange ? (event: SyntheticEvent, value: string) => onInputChange(value) : undefined;

  return (
    <Muiautocomplete
      disablePortal={disablePortal}
      fullWidth
      disabled={disabled}
      size={'small'}
      options={options ?? []}
      noOptionsText={(inputValue && isDebouncing) ? (<>{t('lookingMatches')}</>) : noOptionsText}
      value={value ?? null}
      getOptionLabel={optionLabel}
      onChange={(event, value) => onSelect(value)}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      filterOptions={(option) => option.filter((option) => option?.name?.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase()))}
      isOptionEqualToValue={(option, value) => option === value}
      id='custom-autocomplete'
      data-testid='custom-autocomplete'
      renderInput={(params) => (
        <div style={{ position: 'relative' }}>
          <TextField
            {...params}
            fullWidth
            error={disabled ? undefined : !!error}
            label={error ?? placeholder}
            inputProps={{
              ...params.inputProps,
              maxLength
            }}
          />
          {(inputValue && !value && isDebouncing) && (
            <div style={{ position: 'absolute', top: '58%', right: value ? '55px' : '30px', transform: 'translateY(-50%)' }}>
              <CircularProgress color="primary" size={20} />
            </div>
          )}
        </div>
      )}
      renderOption={(props, option) => <ListItem {...props} key={option?.name}>
        {optionLabel(option)}
      </ListItem>}
    />
  );
};
